/*
 * loadMore — Level 2 (CSP build) zero-JS-capable "Load More" pagination.
 *
 * The SERVER renders CUMULATIVE slices per a `?page=N` query param — page N holds
 * items 1..N·perPage — so the "Load More" control is an ORDINARY GET <a href>. With
 * no JS it navigates to the next cumulative page (a full, working page with more
 * items). The island only removes the reload: it fetches its OWN link href, and
 * because the fetched page's grid is the same cumulative slice a browser navigation
 * would land on, it `replaceChildren()`s the current grid with the fetched grid's
 * children — IDEMPOTENT, and it never invents markup. It then retires the link when
 * the server stops emitting one (exhausted), and falls back to a real navigation if
 * the fetch fails.
 *
 * It NEVER re-initialises Alpine on the spliced grid: the grid items are plain
 * server-rendered HTML with no Alpine directives, so `replaceChildren` is enough and
 * `Alpine.initTree` must not run on them.
 *
 * Generic hooks (project-agnostic):
 *   [data-loadmore]        the island root (x-data="loadMore")
 *   [data-grid]            the grid whose children are replaced with the next slice
 *   [data-loadmore-link]   the GET <a href> (absent once the server has no more)
 *
 * Registered in resources/js/app.js as: Alpine.data('loadMore', loadMore).
 */
export default function loadMore() {
    return {
        _loading: false,

        init() {},

        /**
         * @param {Event} event  Passed automatically by Alpine's `@click="more"` on the link.
         */
        async more(event) {
            event.preventDefault();

            const link = event.currentTarget;
            const url = link?.getAttribute('href');
            if (!url || this._loading) {
                return;
            }

            const grid = this.$root.querySelector('[data-grid]');
            if (!grid) {
                // No grid to splice into — let the link navigate normally.
                window.location.assign(url);
                return;
            }

            this._loading = true;
            link.setAttribute('aria-busy', 'true');

            try {
                const response = await fetch(url, {
                    headers: { 'X-Requested-With': 'fetch' },
                    credentials: 'same-origin',
                });
                if (!response.ok) {
                    throw new Error(`Load More fetch failed: ${response.status}`);
                }

                const doc = new DOMParser().parseFromString(await response.text(), 'text/html');
                const nextGrid = doc.querySelector('[data-grid]');
                if (!nextGrid) {
                    throw new Error('Load More response has no [data-grid]');
                }

                // Cumulative slice → replace the whole grid with the fetched full set.
                // Idempotent, and Alpine is never re-initialised on these plain nodes.
                grid.replaceChildren(...nextGrid.children);

                // Advance or retire the link from the fetched page's own control.
                const nextLink = doc.querySelector('[data-loadmore-link]');
                if (nextLink) {
                    link.setAttribute('href', nextLink.getAttribute('href') ?? '');
                    link.removeAttribute('aria-busy');
                } else {
                    link.remove(); // exhausted — retire the control
                }
            } catch {
                // Network / parse failure → fall back to a real navigation so the user
                // still reaches the next cumulative page.
                window.location.assign(url);
            } finally {
                this._loading = false;
            }
        },

        destroy() {},
    };
}
